import { useEffect, useState } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import isEqual from 'lodash.isequal';
import { OnSelectChange } from '@ui/src';
import {
  useUpdateAppToAppMutation,
  useUpdateAppToAppStatusMutation,
  useUpdateCorporateQuestionnarieMutation,
} from '../../../../hoc/new-application/store/services';
import {
  AccessRight,
  ApplicationScheme,
  CofApprovalStatus,
  NewApplicationStatus,
} from '../../../../hoc/common/types';
import {
  CorporateFormValues,
  NewApplicationChildrenInfo,
  NewApplicationListItem,
} from '../../../../hoc/new-application/types';
import getCorporateAppicationBody from '../../../../hoc/new-application/corporate/helpers/get-corporate-application-body';
import { CorporateQuestionnarieFormValues } from '../../../../hoc/corporate/types';
import getCorporateQuestionnarieBody from '../../../../hoc/new-application/corporate/helpers/get-questionnarie-body';

const useCompanyOfficial = ({
  values,
  index,
  setValue,
  setValuesList,
  value,
  companyOfficial,
  fields,
  applicationId,
  initialFields,
  onApplicationSubmit,
  questionnarieFields,
  defaultQuestionnarieFormValues,
  applicationScheme,
  onSaveAccountsCurrencies,
  onSaveAccountsFlows,
  onSaveExpectedFundsOrigins,
  getApplicationChat,
  type,
}: {
  type: 'MR' | 'COF';
  getApplicationChat?: (data: string) => void;
  onSaveAccountsCurrencies: () => void;
  onSaveAccountsFlows: () => void;
  onSaveExpectedFundsOrigins: () => void;
  applicationScheme?: ApplicationScheme;
  questionnarieFields?: CorporateQuestionnarieFormValues;
  defaultQuestionnarieFormValues?: CorporateQuestionnarieFormValues;
  onApplicationSubmit: ({
    id,
    body,
  }: {
    id: string;
    body: CorporateFormValues;
  }) => void;
  initialFields?: CorporateFormValues;
  applicationId?: string;
  fields?: CorporateFormValues;
  values: {
    id: string;
    position: string;
    percentage: string;
    natureOfControl: string;
    signatureType: string;
    status?: NewApplicationStatus;
    accessRight: AccessRight;
  }[];
  index: number;
  setValue: UseFormSetValue<CorporateFormValues>;
  setValuesList: (
    data: {
      id: string;
      position: string;
      percentage: string;
      natureOfControl: string;
      signatureType: string;
      status?: NewApplicationStatus;
      accessRight: AccessRight;
    }[],
  ) => void;
  value: {
    id: string;
    position: string;
    percentage: string;
    natureOfControl: string;
    signatureType: string;
    status?: NewApplicationStatus;
    accessRight: AccessRight;
  };
  companyOfficial?: NewApplicationChildrenInfo;
}) => {
  const [collapsed, setCollapsed] = useState(true);
  const [companyOfficialPosition, setCompanyOfficialPosition] = useState('');
  const [companyOfficialSignatureType, setCompanyOfficialSignatureType] =
    useState('');
  const [companyOfficialPercentage, setCompanyOfficialPercentage] =
    useState('');
  const [companyOfficialNatureOfControl, setCompanyOfficialNatureOfControl] =
    useState('');
  const [companyOfficialAccessRight, setCompanyOfficialAccessRight] = useState(
    AccessRight.NONE,
  );
  const [error, setError] = useState('');
  const [havingControl, setHavingControl] = useState(false);
  const [focused, setFocused] = useState(false);
  const [defaultValues, setDefaultValues] = useState<any>({});
  const [cofUpdateInProgress, setCofUpdateInProgress] = useState(false);

  const [update, { isLoading: updateInProgress, isError }] =
    useUpdateAppToAppMutation();
  const [updateStatus, { isLoading: updateStatusInProgress }] =
    useUpdateAppToAppStatusMutation();
  const [updateCorporateQuestionnarie] =
    useUpdateCorporateQuestionnarieMutation();

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement> | OnSelectChange,
  ) => {
    const nextValues = values.map((item, valueIndex) => {
      const result = { ...item };

      if (index === valueIndex) {
        if (e.target.name === `customerId-${index}`) {
          result.id = e.target.value as string;
        } else if (e.target.name === `percentage-${index}`) {
          result.percentage = e.target.value as string;
          setCompanyOfficialPercentage(e.target.value as string);
        } else if (e.target.name === `natureOfControl-${index}`) {
          result.natureOfControl = e.target.value as string;
          setCompanyOfficialNatureOfControl(e.target.value as string);
        } else if (e.target.name === `signatureType-${index}`) {
          result.signatureType = e.target.value as string;
          setCompanyOfficialSignatureType(e.target.value as string);
        } else if (e.target.name === `accessRight-${index}`) {
          result.accessRight = e.target.value as AccessRight;
          setCompanyOfficialAccessRight(e.target.value as AccessRight);
        } else {
          result.position = e.target.value as string;
          setCompanyOfficialPosition(e.target.value as string);
        }
      }

      return result;
    });

    setValue(
      type === 'COF' ? 'companyOfficial' : 'mainRepresentative',
      nextValues as NewApplicationChildrenInfo[],
    );
    setValuesList(nextValues);
  };

  useEffect(() => {
    if (companyOfficialPosition && !value.position) {
      const nextValues = values.map((item, valueIndex) => {
        const result = { ...item };

        if (index === valueIndex) {
          result.position = companyOfficialPosition;
        }

        return result;
      });

      setValue('companyOfficial', nextValues as NewApplicationChildrenInfo[]);
      setValuesList(nextValues);
    }
  }, [companyOfficialPosition, value, index]);

  const onUpdate = async () => {
    setError('');

    const body: {
      percentage?: string;
      natureOfControl?: string;
      position?: string;
      signatureType?: string;
      accessRight: AccessRight;
    } = {
      accessRight: companyOfficialAccessRight,
    };

    if (companyOfficialPosition) {
      body.position = companyOfficialPosition;
    }

    if (companyOfficialPercentage) {
      body.percentage = companyOfficialPercentage;
    }

    if (companyOfficialNatureOfControl) {
      body.natureOfControl = companyOfficialNatureOfControl;
    }

    if (companyOfficialSignatureType) {
      body.signatureType = companyOfficialSignatureType;
    }

    let response;

    const nextFields = { ...fields };

    if (companyOfficial?.status !== CofApprovalStatus.DECLINED) {
      response = (await update({
        id: applicationId as string,
        appToAppId: companyOfficial?.id as string,
        body,
      })) as {
        data?: { data: NewApplicationListItem };
        error?: { data: { message: string } };
      };

      delete nextFields?.mainRepresentative;
      delete nextFields?.companyOfficial;

      if (response?.error?.data?.message) {
        setError(response.error.data.message);
      }
    }

    if (!isEqual(nextFields, initialFields)) {
      const applictionBody = getCorporateAppicationBody(nextFields);

      if (!applictionBody.websiteLink) {
        applictionBody.websiteLink = '';
      }

      onApplicationSubmit({
        body: applictionBody,
        id: applicationId as string,
      });
    }

    if (!isEqual(questionnarieFields, defaultQuestionnarieFormValues)) {
      if (
        applicationScheme &&
        (applicationScheme?.accountantDetailsActive ||
          applicationScheme?.auditedByExternalComplianceActive ||
          applicationScheme?.changesInManagementOrOwnershipActive ||
          applicationScheme?.complianceConsultantDetailsActive ||
          applicationScheme?.fcaNumberActive ||
          applicationScheme?.hmrcMlrNumberActive ||
          applicationScheme?.hmrcRegistrationExpiryDateActive ||
          applicationScheme?.icoNumberActive ||
          applicationScheme?.itProviderDetailsActive ||
          applicationScheme?.mlroDetailsActive ||
          applicationScheme?.numberOfAgentsActive ||
          applicationScheme?.numberOfBranchesActive ||
          applicationScheme?.onboardCorporateClientsActive ||
          applicationScheme?.onboardMsbActive ||
          applicationScheme?.paymentsForCharitiesActive ||
          applicationScheme?.payoutPartnerDetailsActive ||
          applicationScheme?.regulatoryInvestigationActive ||
          applicationScheme?.screeningActive ||
          applicationScheme?.transactionMonitoringActive ||
          applicationScheme?.typeOfBusinessLicenceActive ||
          applicationScheme?.visitedByAuthoritiesActive ||
          applicationScheme?.workingWithAnyOtherIPSPActive ||
          applicationScheme?.id === 'cdbb8c71-90e6-4a5e-964f-e55169e0c4df')
      ) {
        const questionnarieBody = getCorporateQuestionnarieBody({
          questionnarieValues:
            questionnarieFields as CorporateQuestionnarieFormValues,
          scheme: applicationScheme,
        });

        updateCorporateQuestionnarie({
          id: applicationId as string,
          body: questionnarieBody,
        });

        if (applicationScheme?.id === 'cdbb8c71-90e6-4a5e-964f-e55169e0c4df') {
          onSaveAccountsCurrencies();
          onSaveAccountsFlows();
          onSaveExpectedFundsOrigins();
        }
      }
    }

    return response;
  };

  const onUpdateStatus = async (status: CofApprovalStatus) => {
    let statusUpdateAllowed = true;

    if (
      !isEqual(defaultValues, {
        position: companyOfficialPosition,
        percentage: companyOfficialPercentage,
        natureOfControl: companyOfficialNatureOfControl,
        signatureType: companyOfficialSignatureType,
        accessRight: companyOfficialAccessRight,
      })
    ) {
      setCofUpdateInProgress(true);
      statusUpdateAllowed = false;

      const response = await onUpdate();

      if (response) {
        statusUpdateAllowed = true;

        setCofUpdateInProgress(false);
      } else if (companyOfficial?.status === CofApprovalStatus.DECLINED) {
        statusUpdateAllowed = true;

        setCofUpdateInProgress(false);
      }
    }

    if (statusUpdateAllowed) {
      const response = (await updateStatus({
        id: applicationId as string,
        appToAppId: companyOfficial?.id as string,
        body: {
          status,
        },
      })) as {
        error?: { data: { message: string } };
      };

      if (response?.error?.data?.message) {
        setError(response.error.data.message);
      } else if (
        !response.error &&
        (status === CofApprovalStatus.ACCEPTED ||
          status === CofApprovalStatus.DECLINED) &&
        getApplicationChat
      ) {
        getApplicationChat(applicationId as string);
      }
    }
  };

  return {
    updateStatusInProgress,
    cofUpdateInProgress,
    onUpdateStatus,
    setHavingControl,
    setCompanyOfficialPosition,
    setCompanyOfficialNatureOfControl,
    setCompanyOfficialPercentage,
    setCompanyOfficialSignatureType,
    setCompanyOfficialAccessRight,
    setDefaultValues,
    isError,
    defaultValues,
    companyOfficialPercentage,
    setError,
    focused,
    setCollapsed,
    collapsed,
    setFocused,
    updateInProgress,
    companyOfficialPosition,
    onChange,
    companyOfficialSignatureType,
    companyOfficialAccessRight,
    havingControl,
    error,
    companyOfficialNatureOfControl,
    onUpdate,
  };
};

export default useCompanyOfficial;
